import React, { useState, useContext } from "react";
// import dayjs from "dayjs";
import firebase from "../../firebase";
import { useHistory, useLocation } from "react-router-dom";

// Material UI Components
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { Helmet } from "react-helmet";

// My Files
import { DatabaseContext } from "../../context";
import { insetSurface } from "../../surfaceTints";
import ResetPasswordDialog from "./ResetPasswordDialog";

export function getSignInErrorMessage(errorCode) {
    switch (errorCode) {
        case "auth/invalid-email":
            return "Please enter a valid email address.";
        case "auth/user-not-found":
        case "auth/wrong-password":
        case "auth/invalid-credential":
        case "auth/invalid-login-credentials":
            return "The email address or password is incorrect. Please try again.";
        case "auth/user-disabled":
            return "This account has been disabled. Please contact your administrator for help.";
        case "auth/too-many-requests":
            return "There have been too many unsuccessful sign-in attempts. Please wait a few minutes and try again, or reset your password.";
        case "auth/network-request-failed":
            return "We couldn't connect. Check your internet connection and try again.";
        default:
            return "We couldn't sign you in. Please try again. If the problem continues, contact your administrator.";
    }
}

export default function SignInPage(props) {
    // firebase.auth().signOut();

    const history = useHistory();
    const location = useLocation();

    const { userConsent } = useContext(DatabaseContext);

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [isInProgress, setIsInProgress] = useState(false);
    const [statusIsVisible, setStatusIsVisible] = useState(false);
    const [statusMessage, setStatusMessage] = useState("");

    const [resetPasswordDialogOpen, setResetPasswordDialogOpen] =
        useState(false);

    function handleClick(event) {
        event.preventDefault();

        if (isInProgress || !email.trim() || !password) {
            return;
        }

        setIsInProgress(true);

        firebase
            .auth()
            .signInWithEmailAndPassword(email, password)
            .then((userCredential) => {
                // Signed in
                var user = userCredential.user;

                let matchingDBUser = userConsent.find(
                    (u) => u.uid === user.uid,
                );

                if (matchingDBUser) {
                    if (matchingDBUser.hasConsent) {
                        console.log("Signed In: " + user.email);

                        // // Add login time to user in database
                        // let ref = firebase.database().ref("users");

                        // ref.child(matchingDBUser.key).update({
                        //     lastLogin: dayjs().toString(),
                        // });

                        const urlParams = new URLSearchParams(location.search);

                        const linkURL = urlParams.get("url");

                        if (linkURL) {
                            history.replace(linkURL);
                        } else {
                            history.replace("");
                        }
                    } else {
                        if (matchingDBUser.under18) {
                            setStatusMessage(
                                "A parent, carer or guardian must give consent before you can sign in. They should have received an email containing a link to the consent form.",
                            );
                        } else {
                            setStatusMessage(
                                "You must give consent before you can sign in. You should have received an email containing a link to the consent form.",
                            );
                        }

                        firebase.auth().signOut();
                        setStatusIsVisible(true);
                    }
                } else {
                    setStatusMessage(
                        "We couldn't access this account. It may have been deactivated or the email address may have changed. Please contact your administrator.",
                    );
                    setStatusIsVisible(true);
                }

                setIsInProgress(false);
            })
            .catch((error) => {
                setIsInProgress(false);
                setEmail("");
                setPassword("");
                setStatusMessage(getSignInErrorMessage(error && error.code));
                setStatusIsVisible(true);
            });
    }

    function handleTextInput(event) {
        if (event.target.id === "email") {
            setEmail(event.target.value);
        } else if (event.target.id === "password") {
            setPassword(event.target.value);
        }
    }

    const handleStatusClose = (event, reason) => {
        if (reason === "clickaway") {
            return;
        }

        setStatusIsVisible(false);
    };

    function handleForgotPasswordClick(event) {
        event.preventDefault();
        setResetPasswordDialogOpen(true);
    }

    function handleCloseResetPasswordDialog() {
        setResetPasswordDialogOpen(false);
    }

    const canSignIn = email.trim() !== "" && password !== "";

    return (
        <>
            <Helmet>
                <title>Sign in | Nodiant</title>
                <meta name="description" content="FAW Performance Analysis" />
                <meta
                    name="og:description"
                    content="FAW Performance Analysis."
                />
            </Helmet>

            <Box
                component="main"
                sx={{
                    minHeight: "100vh",
                    boxSizing: "border-box",
                    px: { xs: 1.5, sm: 3 },
                    py: { xs: 2, sm: 4 },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: (theme) =>
                        theme.palette.mode === "dark"
                            ? theme.palette.background.default
                            : "#edf3ee",
                    backgroundImage: (theme) =>
                        theme.palette.mode === "dark"
                            ? "radial-gradient(circle at 8% 12%, rgba(31, 184, 94, 0.10), transparent 27%), radial-gradient(circle at 92% 88%, rgba(31, 184, 94, 0.07), transparent 30%)"
                            : "radial-gradient(circle at 8% 12%, rgba(0, 149, 61, 0.12), transparent 27%), radial-gradient(circle at 92% 88%, rgba(0, 149, 61, 0.08), transparent 30%)",
                }}
            >
                <Paper
                    elevation={8}
                    sx={{
                        boxSizing: "border-box",
                        width: "100%",
                        maxWidth: 1040,
                        minHeight: { md: 620 },
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            md: "minmax(320px, 0.88fr) minmax(460px, 1.12fr)",
                        },
                        overflow: "hidden",
                        borderRadius: { xs: 3, sm: 4 },
                        border: "1px solid",
                        borderColor: "divider",
                        boxShadow: (theme) =>
                            theme.palette.mode === "dark"
                                ? "0 24px 70px rgba(0, 0, 0, 0.55)"
                                : "0 24px 70px rgba(20, 48, 29, 0.16)",
                    }}
                >
                    <Box
                        component="section"
                        aria-label="Nodiant Online"
                        sx={{
                            display: { xs: "none", md: "flex" },
                            position: "relative",
                            overflow: "hidden",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            minWidth: 0,
                            p: 5,
                            color: "common.white",
                            backgroundColor: "rgb(0, 149, 61)",
                            backgroundImage:
                                "linear-gradient(145deg, rgb(0, 126, 51) 0%, rgb(0, 149, 61) 58%, rgb(0, 119, 49) 100%)",
                            "&::before": {
                                content: '""',
                                position: "absolute",
                                width: 330,
                                height: 330,
                                top: -155,
                                right: -170,
                                borderRadius: "50%",
                                border: "58px solid rgba(255, 255, 255, 0.075)",
                            },
                            "&::after": {
                                content: '""',
                                position: "absolute",
                                width: 270,
                                height: 270,
                                left: -165,
                                bottom: -145,
                                borderRadius: "50%",
                                border: "46px solid rgba(255, 255, 255, 0.06)",
                            },
                        }}
                    >
                        <Box
                            sx={{
                                position: "relative",
                                zIndex: 1,
                                display: "flex",
                                alignItems: "center",
                                gap: 1.5,
                            }}
                        >
                            <Box
                                component="img"
                                src="/images/logo512_large.png"
                                alt=""
                                sx={{
                                    width: 64,
                                    height: 64,
                                    borderRadius: 2,
                                    border: "1px solid rgba(255, 255, 255, 0.28)",
                                    boxShadow:
                                        "0 10px 28px rgba(0, 64, 27, 0.22)",
                                }}
                            />
                            <Box>
                                <Typography
                                    sx={{
                                        fontFamily: "DraigRegular",
                                        fontSize: 25,
                                        lineHeight: 1,
                                        letterSpacing: "0.02em",
                                    }}
                                >
                                    Nodiant
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: "rgba(255, 255, 255, 0.72)",
                                        letterSpacing: "0.09em",
                                        textTransform: "uppercase",
                                    }}
                                >
                                    Online
                                </Typography>
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                position: "relative",
                                zIndex: 1,
                                py: 4,
                            }}
                        >
                            <Typography
                                component="p"
                                sx={{
                                    maxWidth: 360,
                                    fontFamily: "DraigRegular",
                                    fontSize: 40,
                                    lineHeight: 1.08,
                                    letterSpacing: "0.01em",
                                }}
                            >
                                Your performance analysis workspace.
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                position: "relative",
                                zIndex: 1,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                gap: 0.5,
                            }}
                        >
                            <Typography
                                variant="caption"
                                sx={{
                                    color: "rgba(255, 255, 255, 0.58)",
                                    letterSpacing: "0.04em",
                                }}
                            >
                                FAW Performance Analysis
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{
                                    color: "rgba(255, 255, 255, 0.72)",
                                    fontWeight: 600,
                                    letterSpacing: "0.04em",
                                }}
                            >
                                Powered by SportsViz
                            </Typography>
                        </Box>
                    </Box>

                    <Box
                        component="section"
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            minWidth: 0,
                            px: { xs: 2.5, sm: 6, md: 7 },
                            py: { xs: 3, sm: 5 },
                            backgroundColor: "background.paper",
                        }}
                    >
                        <Box
                            sx={{
                                display: {
                                    xs: "flex",
                                    md: "none",
                                },
                                alignItems: "center",
                                gap: 1.25,
                                mb: 4,
                            }}
                        >
                            <Box
                                component="img"
                                src="/images/logo512_large.png"
                                alt=""
                                sx={{
                                    width: 50,
                                    height: 50,
                                    borderRadius: 1.75,
                                    boxShadow:
                                        "0 7px 20px rgba(0, 100, 42, 0.18)",
                                }}
                            />
                            <Box>
                                <Typography
                                    sx={{
                                        fontFamily: "DraigRegular",
                                        fontSize: 23,
                                        lineHeight: 1,
                                    }}
                                >
                                    Nodiant
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                        letterSpacing: "0.08em",
                                        textTransform: "uppercase",
                                    }}
                                >
                                    Online
                                </Typography>
                            </Box>
                        </Box>

                        <Typography
                            component="h1"
                            sx={{
                                color: "text.primary",
                                fontFamily: "DraigRegular",
                                fontSize: { xs: 32, sm: 38 },
                                lineHeight: 1.08,
                                letterSpacing: "0.01em",
                            }}
                        >
                            Welcome back
                        </Typography>

                        <Typography
                            sx={{
                                mt: 1,
                                color: "text.secondary",
                                fontSize: 15,
                                lineHeight: 1.55,
                            }}
                        >
                            Sign in to continue to your Nodiant Online account.
                        </Typography>

                        {statusIsVisible && (
                            <Alert
                                severity="error"
                                variant="outlined"
                                onClose={handleStatusClose}
                                sx={{
                                    mt: 3,
                                    borderRadius: 2,
                                    alignItems: "center",
                                    borderColor: (theme) =>
                                        alpha(theme.palette.error.main, 0.32),
                                    backgroundColor: (theme) =>
                                        alpha(theme.palette.error.main, 0.07),
                                    "& .MuiAlert-message": {
                                        py: 0.25,
                                        fontSize: 14,
                                        lineHeight: 1.5,
                                    },
                                }}
                            >
                                {statusMessage}
                            </Alert>
                        )}

                        <Box
                            component="form"
                            noValidate
                            onSubmit={handleClick}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                                mt: statusIsVisible ? 2.5 : 4,
                            }}
                        >
                            <TextField
                                value={email}
                                onChange={handleTextInput}
                                variant="outlined"
                                required
                                fullWidth
                                id="email"
                                label="Email address"
                                name="email"
                                autoComplete="email"
                                autoFocus
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <EmailOutlinedIcon
                                                color="action"
                                                fontSize="small"
                                            />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        minHeight: 54,
                                        borderRadius: 2,
                                        backgroundColor: insetSurface,
                                    },
                                }}
                            />
                            <TextField
                                value={password}
                                onChange={handleTextInput}
                                variant="outlined"
                                required
                                fullWidth
                                name="password"
                                label="Password"
                                type={showPassword ? "text" : "password"}
                                id="password"
                                autoComplete="current-password"
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <LockOutlinedIcon
                                                color="action"
                                                fontSize="small"
                                            />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label={
                                                    showPassword
                                                        ? "Hide password"
                                                        : "Show password"
                                                }
                                                onClick={() =>
                                                    setShowPassword(
                                                        (current) => !current,
                                                    )
                                                }
                                                edge="end"
                                            >
                                                {showPassword ? (
                                                    <VisibilityOffOutlinedIcon />
                                                ) : (
                                                    <VisibilityOutlinedIcon />
                                                )}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        minHeight: 54,
                                        borderRadius: 2,
                                        backgroundColor: insetSurface,
                                    },
                                }}
                            />

                            <Box sx={{ mt: -0.5, textAlign: "right" }}>
                                <Link
                                    component="button"
                                    type="button"
                                    onClick={handleForgotPasswordClick}
                                    underline="hover"
                                    sx={{
                                        fontSize: 14,
                                        fontWeight: 700,
                                        color: "primary.dark",
                                    }}
                                >
                                    Forgot password?
                                </Link>
                            </Box>

                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                disableElevation
                                disabled={!canSignIn || isInProgress}
                                startIcon={
                                    isInProgress ? (
                                        <CircularProgress
                                            size={18}
                                            color="inherit"
                                        />
                                    ) : null
                                }
                                sx={{
                                    mt: 1,
                                    minHeight: 50,
                                    borderRadius: 2,
                                    fontFamily: "DraigRegular",
                                    fontSize: 18,
                                    letterSpacing: "0.025em",
                                    textTransform: "none",
                                    boxShadow:
                                        "0 8px 20px rgba(0, 149, 61, 0.2)",
                                    "&:hover": {
                                        // Undefined in dark leaves the themed
                                        // contained-button hover in charge.
                                        backgroundColor: (theme) =>
                                            theme.palette.mode === "dark"
                                                ? undefined
                                                : "rgb(0, 125, 51)",
                                        boxShadow:
                                            "0 10px 24px rgba(0, 125, 51, 0.24)",
                                    },
                                    "&.Mui-disabled": {
                                        boxShadow: "none",
                                    },
                                }}
                            >
                                {isInProgress ? "Signing in…" : "Sign in"}
                            </Button>
                        </Box>

                        <Box
                            sx={{
                                display: { xs: "block", md: "none" },
                                mt: 4,
                                pt: 2.5,
                                borderTop: "1px solid",
                                borderColor: "divider",
                                textAlign: "center",
                            }}
                        >
                            <Typography
                                variant="caption"
                                sx={{
                                    color: "text.secondary",
                                    fontSize: 12,
                                    letterSpacing: "0.04em",
                                }}
                            >
                                FAW Performance Analysis
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{
                                    display: "block",
                                    mt: 0.5,
                                    color: "primary.dark",
                                    fontSize: 12,
                                    fontWeight: 600,
                                    letterSpacing: "0.04em",
                                }}
                            >
                                Powered by SportsViz
                            </Typography>
                        </Box>
                    </Box>
                </Paper>
            </Box>

            <ResetPasswordDialog
                open={resetPasswordDialogOpen}
                onClose={handleCloseResetPasswordDialog}
                email={email}
            />
        </>
    );
}
